// src/library/utilities/designLoader.ts

import { Bridge } from "@/library/bridge";
import { Channel } from "@/library/channel";
import ComponentPolygon from "@/library/componentPolygon";
import { InletOutlet } from "@/library/inletOutlet";
import { LevelConnection } from "@/library/levelConnection";
import ModuleTemplate from "@/library/modules/moduleTemplate";
import type {
  BasicPartOutput,
  Bridgeoutput,
  ChannelOutput,
  ComponentCircleOutput,
  ModuleOutput,
  PolygonOutput
} from "@/library/typeDefinations";
import { useContentStore } from "../../stores/content";
import { useControlStore } from "../../stores/control";
import { useMoudleStore } from "../../stores/module";
import ComponentCircle from "../componentCircle";


import {
  loadUserComponents,
  loadUserModuleTemplates,
  registerUserComponent,
  addUserModuleTemplate
} from "@/library/userModuleRegistry";
import { createUserTemplateCreator } from "@/library/modules/userTemplateCreator";
import type { UserPresetTemplate } from "@/types/userPresetTemplate";
import type { UserComponentJson } from "@/types/userComponent";

type DesignFile = {
  title: string;
  chipProperties: number[];
  layers: Array<{ height: number; label: string; id: string; contentIds: string[] }>;
  content: {
    modules: ModuleOutput[];
    polygons: PolygonOutput[];
    circles: ComponentCircleOutput[];
    inOutlets: BasicPartOutput[];
    channels: ChannelOutput[];
    bridges: Bridgeoutput[];
    levelConnections: BasicPartOutput[];
  };
  userAssets?: {
    components?: UserComponentJson[];
    templates?: UserPresetTemplate[];
  };
};

function safeParseJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch (e) {
    console.error("[designLoader] JSON.parse failed", e);
    return null;
  }
}

function collectUsedUserAssets(modules: ModuleOutput[]) {
  const usedTemplateIds = new Set<string>();
  const usedComponentIds = new Set<string>();

  for (const m of modules || []) {
    if (m?.templateId) usedTemplateIds.add(String(m.templateId));
    if (m?.componentId) usedComponentIds.add(String(m.componentId));
  }


  const allTemplates = loadUserModuleTemplates() || [];
  const allComponents = loadUserComponents() || [];

  const templates = allTemplates.filter((t) => usedTemplateIds.has(String(t.id)));


  const components = allComponents.filter((c: any) => {
    const cid = String(c?.id ?? c?.name ?? "");
    return usedComponentIds.has(cid);
  });

  return { templates, components, usedTemplateIds, usedComponentIds };
}


function restoreUserAssetsFromDesign(design: DesignFile) {
  const moduleStore = useMoudleStore();

  const comps = design.userAssets?.components || [];
  const tpls = design.userAssets?.templates || [];

  // 1) restore components
  if (comps.length > 0) {
    comps.forEach((c) => {
      try {
        registerUserComponent(c);
      } catch (e) {
        console.warn("[designLoader] registerUserComponent failed", c?.name, e);
      }
    });
  }

  // 2) restore templates
  if (tpls.length > 0) {
    tpls.forEach((t) => {
      try {
        
        addUserModuleTemplate(t);

        
        let baseCreator: any = moduleStore.moduleFactory?.[t.baseModuleIdx];

       
        if (!baseCreator) {
          baseCreator = moduleStore.moduleFactory?.find((c: any) => {
            if (!c) return false;
            if (typeof c.getModuleIndex === "function") {
              return c.getModuleIndex() === t.baseModuleIdx;
            }
            if (typeof c.getModuleIndex === "number") {
              return c.getModuleIndex === t.baseModuleIdx;
            }
            if (c.pendingModule && typeof c.pendingModule.getModuleIndex === "number") {
              return c.pendingModule.getModuleIndex === t.baseModuleIdx;
            }
            return false;
          });
        }

        if (!baseCreator) {
          console.warn("[designLoader] baseCreator not found for template", t);
          return;
        }

       
        const wrapper = createUserTemplateCreator(t as any, baseCreator);
        if (!wrapper) {
          console.warn("[designLoader] createUserTemplateCreator returned null", t);
          return;
        }

        moduleStore.moduleFactory.push(wrapper);
        if (typeof (moduleStore as any).registerUserCreatorInMap === "function") {
          (moduleStore as any).registerUserCreatorInMap(wrapper);
        }
      } catch (e) {
        console.warn("[designLoader] restore template failed", t?.name, e);
      }
    });
  }
}

const loadDesign = (file: Blob): void => {
  const controlStore = useControlStore();
  controlStore.resetAppSettings();

  const contentStore = useContentStore();
  const moduleStore = useMoudleStore();

  const reader = new FileReader();

  reader.onload = () => {
    try {
      console.log("[designLoader] loaded");

      if (typeof reader.result !== "string") {
        throw new Error("invalid file!");
      }

      const raw = safeParseJson(reader.result);
      if (!raw) throw new Error("invalid JSON!");

      const design = raw as DesignFile;

      
      contentStore.initializeContent(design.chipProperties);
      contentStore.title = design.title;

     
      restoreUserAssetsFromDesign(design);

      // parse content: levelConnections
      design.content.levelConnections?.forEach((levelConnection: BasicPartOutput) => {
        const res = LevelConnection.parse(levelConnection);
        contentStore.contentMap.set(res.id, res);
        contentStore.levelConnectionIds.push(res.id);
      });

      // parse content: modules (stable-id first, safe)
      design.content.modules?.forEach((module: ModuleOutput) => {
        const creator =
          typeof (moduleStore as any).getCreatorForModuleOutput === "function"
            ? (moduleStore as any).getCreatorForModuleOutput(module)
            : moduleStore.moduleFactory?.[module.moduleIdx];

        const lookupHit =
          !!(module?.templateId || module?.componentId) &&
          !!creator &&
          (module?.templateId || module?.componentId);

        if (!creator) {
          console.error("[loadDesign] Unknown module creator", {
            componentId: module.componentId,
            templateId: module.templateId,
            moduleIdx: module.moduleIdx,
            factoryLen: moduleStore.moduleFactory?.length,
            lookupHit: !!lookupHit,
            module
          });
          return;
        }

        const parseFn =
          typeof creator.parseModule === "function"
            ? creator.parseModule.bind(creator)
            : typeof creator.parse === "function"
              ? creator.parse.bind(creator)
              : null;

        if (!parseFn) {
          console.error("[loadDesign] Creator has no parseModule/parse", {
            creatorName: creator.moduleName,
            componentId: module.componentId,
            templateId: module.templateId,
            moduleIdx: module.moduleIdx,
            module
          });
          return;
        }

        try {
          const res = parseFn(module);
          contentStore.contentMap.set(res.id, res);
        } catch (err) {
          console.error("[loadDesign] parseModule failed", {
            moduleIdx: module.moduleIdx,
            creatorName: creator.moduleName,
            templateId: module.templateId,
            componentId: module.componentId,
            properties: module.properties,
            module
          }, err);
        }
      });

      // polygons
      design.content.polygons?.forEach((polygon: PolygonOutput) => {
        const res = ComponentPolygon.parse(polygon);
        contentStore.contentMap.set(res.id, res);
      });

      // circles
      design.content.circles?.forEach((circle: ComponentCircleOutput) => {
        const res = ComponentCircle.parse(circle);
        contentStore.contentMap.set(res.id, res);
      });

      // inOutlets
      design.content.inOutlets?.forEach((inOutlet: BasicPartOutput) => {
        const res = InletOutlet.parse(inOutlet);
        contentStore.contentMap.set(res.id, res);
      });

      // channels
      design.content.channels?.forEach((channel: ChannelOutput) => {
        const res = Channel.parse(channel);
        contentStore.contentMap.set(res.id, res);
      });

      // bridges
      design.content.bridges?.forEach((bridge: Bridgeoutput) => {
        const res = Bridge.parse(bridge);
        contentStore.contentMap.set(res.id, res);
      });

      // parse layers
      contentStore.levelCount = design.layers?.length ?? 0;
      contentStore.layers = new Map<
        string,
        {
          height: number;
          color: string;
          label: string;
          id: string;
          contentIds: string[];
        }
      >();

      design.layers?.forEach(
        (
          layer: {
            height: number;
            label: string;
            id: string;
            contentIds: string[];
          },
          idx: number
        ) => {
          const newLayer = {
            height: layer.height,
            color: contentStore.getColorByIdx(idx),
            label: layer.label,
            id: layer.id,
            
            contentIds: (layer.contentIds ?? []).filter((id) => {
              const elm = contentStore.getElement(id);
              return elm && elm.display;
            })
          };
          contentStore.layers.set(layer.id, newLayer);
        }
      );
    } catch (err) {
      console.error("[loadDesign] failed:", err);
    }
  };

  reader.readAsText(file);
};

const generateDesign = (): void => {
  const contentStore = useContentStore();

  const chip: DesignFile = {
    title: contentStore.title,
    layers: Array.from(contentStore.layers.values()).map((layer) => ({
      height: layer.height,
      label: layer.label,
      id: layer.id,
      contentIds: layer.contentIds
    })),
    chipProperties: contentStore.chipProperties.map((p) => p.value),
    content: {
      modules: Array<ModuleOutput>(),
      polygons: Array<PolygonOutput>(),
      circles: Array<ComponentCircleOutput>(),
      inOutlets: Array<BasicPartOutput>(),
      channels: Array<ChannelOutput>(),
      bridges: Array<Bridgeoutput>(),
      levelConnections: Array<BasicPartOutput>()
    }
  };

  // don't add hidden elements
  contentStore.layers.forEach((layer, layerId) => {
    layer.contentIds.forEach((id) => {
      const elm = contentStore.getElement(id);
      if (elm && elm.display) {
        if (elm instanceof ModuleTemplate) {
          chip.content.modules.push(elm.buildOutput(layerId));
        } else if (elm instanceof Channel) {
          chip.content.channels.push(elm.buildOutput(layerId));
        } else if (elm instanceof InletOutlet) {
          chip.content.inOutlets.push(elm.buildOutput(layerId));
        } else if (elm instanceof ComponentPolygon) {
          chip.content.polygons.push(elm.buildOutput(layerId));
        } else if (elm instanceof Bridge) {
          chip.content.bridges.push(elm.buildOutput(layerId));
        } else if (elm instanceof ComponentCircle) {
          chip.content.circles.push(elm.buildOutput(layerId));
        }
      }
    });
  });

  contentStore.levelConnectionIds.forEach((id) => {
    const elm = contentStore.getElement(id);
    if (elm && elm.display && elm instanceof LevelConnection) {
      chip.content.levelConnections.push(elm.buildOutput());
    }
  });

  
  const { templates, components } = collectUsedUserAssets(chip.content.modules);
  if (templates.length > 0 || components.length > 0) {
    chip.userAssets = {
      templates,
      components
    };
  }

  // download design
  const textBlob = new Blob([JSON.stringify(chip)], {
    type: "text/plain"
  });
  const a = document.createElement("a");
  a.download = `${contentStore.title}.json`;
  a.href = window.URL.createObjectURL(textBlob);
  a.click();
};

export default { loadDesign, generateDesign };



